/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {memo, MutableRefObject, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {MAPVIEW_ZOOM_LEVEL_SELECTED_ASSET, mapViewGoToOptions} from '@ion/dls/src/maps/constants';
import {isMapSelectionSelector, selectedAssetSelector} from '@/store/selectors/tracking-selectors';
import {getItemValue} from '../../../tracking/trackingUtils';

export interface SelectedAssetFocusProps {
  mapViewRef: MutableRefObject<__esri.MapView>;
}

/**
 * Centers and zooms the tracking map on a newly selected asset - when it was
 * not selected through a map click - and closes any open popup. It renders
 * nothing: it lives in its own component so the selected asset, which is
 * refreshed on every update cycle, re-renders only this and not the whole
 * tracking map.
 *
 * ```typescript
 *
 *   import SelectedAssetFocus from './SelectedAssetFocus';
 *   ...
 *
 *   <SelectedAssetFocus mapViewRef={mapviewRef} />
 *
 * ```
 *
 * @param   mapViewRef The tracking map's view, once it is created.
 * @returns            Nothing; the map is moved as a side effect.
 */
export const SelectedAssetFocus = ({mapViewRef}: SelectedAssetFocusProps) => {
  const selectedAsset    = useSelector(selectedAssetSelector);
  const isMapSelection   = useSelector(isMapSelectionSelector);
  const selectedAssetRef = useRef(null);

  useEffect(() => {
    if (mapViewRef.current && selectedAsset) {
      const {assetId}                  = getItemValue(selectedAsset);
      const {assetId: previousAssetId} = getItemValue(selectedAssetRef.current) || {};
      if (!isMapSelection && assetId !== previousAssetId) {
        const {longitude, latitude} = getItemValue(selectedAsset) || {};
        mapViewRef.current.zoom     = MAPVIEW_ZOOM_LEVEL_SELECTED_ASSET;
        mapViewRef.current.goTo({center: [longitude, latitude]}, mapViewGoToOptions);
        mapViewRef.current.popup?.close();
      }
    }
    selectedAssetRef.current = selectedAsset;
  }, [selectedAsset, isMapSelection]);

  return null;
};

export default memo(SelectedAssetFocus);
